import React, { useState, useMemo, useCallback, memo } from "react";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { Grid2 } from "@mui/material";
import { useStyles } from "./styles";
import { useTheme } from "@emotion/react";
import Tickbox from "components/tickbox";
import PropTypes from "prop-types";
import SearchField from "components/simpleSearch";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";

function toggleValue(list, value) {
  if (list.includes(value)) {
    return list.filter((v) => v !== value);
  }
  return [...list, value];
}

function sortEnabledFirst(arr) {
  return [...arr].sort((a, b) => {
    if (!!a.disabled === !!b.disabled) {
      const av = a.value?.toString() ?? "";
      const bv = b.value?.toString() ?? "";
      return av.localeCompare(bv, undefined, { sensitivity: "base" });
    }
    return a.disabled ? 1 : -1;
  });
}

function toggleAll(currentChecked, itemsValues, hasAll) {
  if (hasAll) {
    const removeSet = new Set(itemsValues);
    return currentChecked.filter((v) => !removeSet.has(v));
  }
  const existing = new Set(currentChecked);
  const additions = itemsValues.filter((v) => !existing.has(v));
  return [...currentChecked, ...additions];
}

const CustomList = memo(function CustomList({
  title,
  items,
  searchValue,
  setSearchValue,
  onToggleAll,
  onToggleItem,
  checkedSet,
  showInfo = false,
  permissions = [],
  openTooltip = null,
  setOpenTooltip,
  classes,
}) {
  const total = items.length;
  const selectedCount = useMemo(
    () =>
      items.reduce(
        (acc, item) => (checkedSet.has(item.value) ? acc + 1 : acc),
        0
      ),
    [items, checkedSet]
  );
  const isAllSelected = total > 0 && selectedCount === total;
  const isIndeterminate = selectedCount > 0 && selectedCount < total;

  return (
    <Grid2 container className={classes.cardsContainer}>
      <Card className={classes.card}>
        <CardHeader
          sx={{ px: 2, py: 1, margin: 0 }}
          className={classes.cardHeader}
          avatar={
            <Tickbox
              id={`select-all-${title}`}
              setValue={onToggleAll}
              isChecked={isAllSelected}
              indeterminate={isIndeterminate}
              disabled={total === 0}
            />
          }
          title={title}
          subheader={`${selectedCount}/${total} selected`}
        />
        <Divider />
        <SearchField
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
        <List
          sx={{
            height: 230,
            overflow: "auto",
          }}
          dense
          component="ul"
        >
          {items.map((item) => {
            const labelId = `transfer-list-all-item-${item.value}-label`;
            const isChecked = checkedSet.has(item.value);
            const toggleItem = onToggleItem(item.value);
            return (
              <ListItemButton
                disabled={item.disabled}
                key={item.value}
                onClick={toggleItem}
              >
                <ListItemIcon
                  className={classes.listItemIcon}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tickbox
                    setValue={toggleItem}
                    id={item.value}
                    isChecked={isChecked}
                    disabled={item.disabled}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={item.value}
                  className={classes.listItemText}
                />
                {showInfo && (
                  <Tooltip
                    title={
                      "Permissions : " +
                      permissions?.map((perm) => perm).join(", ")
                    }
                    open={openTooltip === item.value}
                    onClose={() => setOpenTooltip(null)}
                  >
                    <InfoOutlinedIcon
                      fontSize="small"
                      color="action"
                      sx={{ ml: 1, cursor: "pointer" }}
                      aria-label={`info about ${item.value}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenTooltip(
                          openTooltip === item.value ? null : item.value
                        );
                      }}
                    />
                  </Tooltip>
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Card>
    </Grid2>
  );
});

CustomList.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  setSearchValue: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  onToggleItem: PropTypes.func.isRequired,
  checkedSet: PropTypes.instanceOf(Set).isRequired,
  showInfo: PropTypes.bool,
  permissions: PropTypes.array,
  openTooltip: PropTypes.string,
  setOpenTooltip: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default function TransferList({
  choicesTitle,
  chosenTitle,
  availableList = [],
  assignedList = [],
  setSelectedItems = () => {},
  showInfo = false,
  permissions = [],
  isNavbarOpen,
}) {
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(availableList);
  const [right, setRight] = useState(assignedList);

  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");

  const [openTooltip, setOpenTooltip] = useState(null);
  const checkedSet = useMemo(() => new Set(checked), [checked]);

  const leftChecked = useMemo(
    () => left.filter((item) => checkedSet.has(item.value)),
    [left, checkedSet]
  );
  const rightChecked = useMemo(
    () => right.filter((item) => checkedSet.has(item.value)),
    [right, checkedSet]
  );

  const handleToggle = useCallback(
    (value) => () => {
      setChecked((prev) => toggleValue(prev, value));
    },
    []
  );

  const handleToggleAll = useCallback(
    (items) => () => {
      const itemValues = items.filter((i) => !i.disabled).map((i) => i.value);
      const allSelected = itemValues.every((v) => checkedSet.has(v));
      setChecked((prev) => toggleAll(prev, itemValues, allSelected));
    },
    [checkedSet]
  );

  const handleCheckedRight = useCallback(() => {
    const selectedLeftValueSet = new Set(
      leftChecked.map((leftItem) => leftItem.value)
    );
    const updatedRightList = [
      ...right,
      ...leftChecked.filter(
        (leftItem) =>
          !right.some(
            (existingRightItem) => existingRightItem.value === leftItem.value
          )
      ),
    ];
    const updatedLeftList = left.filter(
      (leftItem) => !selectedLeftValueSet.has(leftItem.value)
    );
    setRight(updatedRightList);
    setLeft(updatedLeftList);
    setChecked(checked.filter((value) => !selectedLeftValueSet.has(value)));
    setSelectedItems(updatedRightList);
  }, [leftChecked, right, left, checked, setSelectedItems]);

  const handleCheckedLeft = useCallback(() => {
    const selectedRightValueSet = new Set(
      rightChecked.map((rightItem) => rightItem.value)
    );
    const updatedLeftList = [
      ...left,
      ...rightChecked.filter(
        (rightItem) =>
          !left.some(
            (existingLeftItem) => existingLeftItem.value === rightItem.value
          )
      ),
    ];
    const updatedRightList = right.filter(
      (rightItem) => !selectedRightValueSet.has(rightItem.value)
    );
    setLeft(updatedLeftList);
    setRight(updatedRightList);
    setChecked(checked.filter((value) => !selectedRightValueSet.has(value)));
    setSelectedItems(updatedRightList);
  }, [rightChecked, right, left, checked, setSelectedItems]);

  const filteredLeft = useMemo(
    () =>
      leftSearch
        ? left.filter((item) =>
            item.value.toLowerCase().includes(leftSearch.toLowerCase())
          )
        : left,
    [left, leftSearch]
  );
  const filteredRight = useMemo(
    () =>
      rightSearch
        ? right.filter((item) =>
            item.value.toLowerCase().includes(rightSearch.toLowerCase())
          )
        : right,
    [right, rightSearch]
  );

  const sortedLeft = useMemo(
    () => sortEnabledFirst(filteredLeft),
    [filteredLeft]
  );
  const sortedRight = useMemo(
    () => sortEnabledFirst(filteredRight),
    [filteredRight]
  );

  return (
    <Grid2 container className={classes.listsWrapper}>
      <CustomList
        title={choicesTitle}
        items={sortedLeft}
        searchValue={leftSearch}
        setSearchValue={setLeftSearch}
        onToggleAll={handleToggleAll(filteredLeft)}
        onToggleItem={handleToggle}
        checkedSet={checkedSet}
        showInfo={showInfo}
        permissions={permissions}
        openTooltip={openTooltip}
        setOpenTooltip={setOpenTooltip}
        classes={classes}
      />
      <Grid2 className={classes.arrowsColumn}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCheckedRight}
          disabled={leftChecked.length === 0}
          aria-label="move selected right"
          className={classes.arrows}
        >
          &gt;
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCheckedLeft}
          disabled={rightChecked.length === 0}
          aria-label="move selected left"
          className={classes.arrows}
        >
          &lt;
        </Button>
      </Grid2>
      <CustomList
        title={chosenTitle}
        items={sortedRight}
        searchValue={rightSearch}
        setSearchValue={setRightSearch}
        onToggleAll={handleToggleAll(filteredRight)}
        onToggleItem={handleToggle}
        checkedSet={checkedSet}
        showInfo={showInfo}
        permissions={permissions}
        openTooltip={openTooltip}
        setOpenTooltip={setOpenTooltip}
        classes={classes}
      />
    </Grid2>
  );
}

TransferList.propTypes = {
  choicesTitle: PropTypes.string.isRequired,
  chosenTitle: PropTypes.string.isRequired,
  availableList: PropTypes.array.isRequired,
  assignedList: PropTypes.array.isRequired,
  setSelectedItems: PropTypes.func.isRequired,
  showInfo: PropTypes.bool,
  permissions: PropTypes.any,
  isNavbarOpen: PropTypes.bool,
};
